import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { DashboardShell } from 'components/DashboardShell';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from 'components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from 'components/ui/avatar';
import { Textarea } from 'components/ui/textarea';
import { Checkbox } from 'components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { StatusBadge } from 'components/StatusBadge';
import { Badge } from 'components/ui/badge';
import { MapPanel } from 'components/MapPanel';
import { ParcelCard } from 'components/ParcelCard';
import { ParcelTypeCheckboxes } from 'components/ParcelTypeCheckboxes';
import { TransportModeIcon } from 'components/TransportModeIcon';
import api from 'lib/api';
import { toast } from 'sonner';
import { useAuth } from 'contexts/AuthContext';
import { useLanguage } from 'lib/i18n';
import { formatPrice, formatDate, TRANSPORT_LABELS, PARCEL_TYPES } from 'lib/format';
import { ArrowLeft, MapPin, Calendar, Weight, MessageCircle, Sparkles, Edit3, Trash2, Star, BadgeCheck, Eye } from 'lucide-react';

const getDeliveryId = (delivery) => {
  const raw =
    delivery?.id ??
    delivery?._id ??
    delivery?.uuid ??
    delivery?.delivery_id ??
    delivery?.delivery_request_id ??
    delivery?.deliveryRequestId ??
    delivery?.delivery_request?.id ??
    delivery?.delivery_request?._id ??
    delivery?.delivery_request?.uuid;
  return raw == null ? '' : String(raw);
};

const extractDeliveries = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const OPEN_STATUSES = new Set(['open', 'active', 'published', 'available', 'pending']);

const isOwnedByUser = (delivery, userId) => {
  if (userId == null) return false;
  const ownerId =
    delivery?.sender_id ??
    delivery?.sender?.id ??
    delivery?.user_id ??
    delivery?.user?.id ??
    delivery?.owner_id ??
    delivery?.owner?.id ??
    delivery?.created_by ??
    delivery?.createdBy ??
    delivery?.delivery_request?.sender_id ??
    delivery?.delivery_request?.sender?.id;
  return ownerId != null && String(ownerId) === String(userId);
};

const getDeliveryTitle = (delivery) => {
  return (
    delivery?.title ??
    delivery?.name ??
    delivery?.label ??
    delivery?.delivery_request?.title ??
    delivery?.delivery_request?.name ??
    ''
  );
};

const getDeliveryWeight = (delivery) => {
  return (
    delivery?.weight_kg ??
    delivery?.weight ??
    delivery?.delivery_request?.weight_kg ??
    delivery?.delivery_request?.weight ??
    '?'
  );
};

const getErrorMessage = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;

  if (Array.isArray(detail)) {
    const first = detail[0];
    if (typeof first === 'string') return first;
    if (first && typeof first.msg === 'string') {
      const field = Array.isArray(first.loc) ? first.loc.join('.') : '';
      return field ? `${field}: ${first.msg}` : first.msg;
    }
  }

  if (detail && typeof detail.msg === 'string') {
    return detail.msg;
  }

  return fallback;
};

const buildTripEditForm = (trip) => ({
  departure_date: trip.departure_date ? trip.departure_date.slice(0, 16) : '',
  arrival_date: trip.arrival_date ? trip.arrival_date.slice(0, 16) : '',
  price_per_kg: trip.price_per_kg != null ? trip.price_per_kg : '',
  flat_price: trip.flat_price != null ? trip.flat_price : '',
  capacity_kg: trip.capacity_kg || 10,
  transport_mode: trip.transport_mode || 'car',
  intermediate_cities: Array.isArray(trip.intermediate_cities) ? trip.intermediate_cities.join(', ') : (trip.intermediate_cities || ''),
  notes: trip.notes || '',
  restrictions: trip.restrictions || '',
  negotiable: trip.negotiable !== false,
  max_dimensions_cm: trip.max_dimensions_cm || '',
  accepted_parcel_types: trip.accepted_parcel_types || [],
});

export default function TripDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const autoEditOpenedFor = useRef('');
  const [trip, setTrip] = useState(null);
  const [travelerProfile, setTravelerProfile] = useState(null);
  const [matches, setMatches] = useState([]);
  const [myDeliveries, setMyDeliveries] = useState([]);
  const [requestOpen, setRequestOpen] = useState(false);
  const [selectedDeliveryId, setSelectedDeliveryId] = useState('');
  const [requestPrice, setRequestPrice] = useState('');
  const [requestMessage, setRequestMessage] = useState('');
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [editTripOpen, setEditTripOpen] = useState(false);
  const [editTripForm, setEditTripForm] = useState({});
  const [savingTripEdit, setSavingTripEdit] = useState(false);

  useEffect(() => {
    api.get(`/trips/${id}`).then(r => setTrip(r.data));
  }, [id]);

  useEffect(() => {
    if (!trip?.traveler_id) return setTravelerProfile(null);
    api.get(`/users/${trip.traveler_id}`).then((r) => setTravelerProfile(r.data)).catch(() => setTravelerProfile(null));
  }, [trip?.traveler_id]);

  useEffect(() => {
    if (!trip || !user || trip.traveler_id !== user.id || searchParams.get('edit') !== '1') return;
    if (autoEditOpenedFor.current === id || !['active', 'completed'].includes(trip.status)) return;
    autoEditOpenedFor.current = id;
    setEditTripForm(buildTripEditForm(trip));
    setEditTripOpen(true);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('edit');
    setSearchParams(nextParams, { replace: true });
  }, [id, searchParams, setSearchParams, trip, user]);

  useEffect(() => {
    if (!trip || !user) return;
    if (trip.traveler_id === user.id) {
      api.get(`/trips/${id}/matches`).then(r => setMatches(r.data)).catch(() => {});
    } else {
      const loadDeliveries = async () => {
        try {
          const mineResp = await api.get('/deliveries?mine=true');
          let deliveries = extractDeliveries(mineResp.data).filter(d => !!getDeliveryId(d));

          // Backend fallback: if mine=true is ignored or unsupported, load all and filter locally.
          if (!deliveries.length) {
            const allResp = await api.get('/deliveries');
            const allDeliveries = extractDeliveries(allResp.data).filter(d => !!getDeliveryId(d));
            deliveries = allDeliveries.filter(d => isOwnedByUser(d, user.id));
          }

          const openDeliveries = deliveries.filter(d => {
            const rawStatus = d?.status ?? d?.state ?? d?.delivery_request?.status ?? d?.delivery_request?.state;
            return OPEN_STATUSES.has(String(rawStatus || '').toLowerCase());
          });
          // Fallback to all owned deliveries if backend uses a different status naming.
          const requestableDeliveries = openDeliveries.length ? openDeliveries : deliveries;
          setMyDeliveries(requestableDeliveries);
          setSelectedDeliveryId(prev => {
            if (!requestableDeliveries.length) return '';
            const keepCurrent = requestableDeliveries.some(d => getDeliveryId(d) === String(prev));
            return keepCurrent ? String(prev) : getDeliveryId(requestableDeliveries[0]);
          });
        } catch {
          setMyDeliveries([]);
          setSelectedDeliveryId('');
        }
      };

      loadDeliveries();
    }
  }, [trip, user, id]);

  useEffect(() => {
    if (!requestOpen || selectedDeliveryId || !myDeliveries.length) return;
    setSelectedDeliveryId(getDeliveryId(myDeliveries[0]));
  }, [requestOpen, selectedDeliveryId, myDeliveries]);

  if (!trip) return <DashboardShell><div>{t('tripDetail.loading')}</div></DashboardShell>;

  const isOwner = user?.id === trip.traveler_id;
  const remaining = trip.capacity_kg - (trip.capacity_used_kg || 0);

  const openTripEditDialog = () => {
    setEditTripForm(buildTripEditForm(trip));
    setEditTripOpen(true);
  };

  const handleSaveTripEdit = async (e) => {
    e.preventDefault();
    const reopening = trip.status === 'completed';
    if (!editTripForm.departure_date) {
      toast.error(t('tripDetail.departureRequired'));
      return;
    }
    setSavingTripEdit(true);
    try {
      const payload = {
        ...editTripForm,
        capacity_kg: Number(editTripForm.capacity_kg),
        price_per_kg: editTripForm.price_per_kg ? Number(editTripForm.price_per_kg) : null,
        flat_price: editTripForm.flat_price ? Number(editTripForm.flat_price) : null,
        intermediate_cities: editTripForm.intermediate_cities
          ? editTripForm.intermediate_cities.split(',').map(s => s.trim()).filter(Boolean)
          : [],
      };
      const res = await api.patch(`/trips/${id}`, payload);
      setTrip(res.data);
      toast.success(t(reopening ? 'tripDetail.reopened' : 'tripDetail.updated'));
      setEditTripOpen(false);
    } catch (err) {
      toast.error(getErrorMessage(err, t('tripDetail.updateError')));
    } finally {
      setSavingTripEdit(false);
    }
  };

  const submitRequest = async () => {
    if (submittingRequest) return;
    const resolvedDeliveryId = selectedDeliveryId || (myDeliveries.length ? getDeliveryId(myDeliveries[0]) : '');
    const deliveryRequestId = resolvedDeliveryId ? String(resolvedDeliveryId) : '';

    setSubmittingRequest(true);
    try {
      const payload = {
        trip_id: id,
        proposed_price: Number(requestPrice || 0),
        message: requestMessage,
      };
      if (deliveryRequestId) payload.delivery_request_id = deliveryRequestId;

      const mission = await api.post('/missions', payload);
      toast.success(t('tripDetail.requestSent'));
      setRequestOpen(false);
      navigate(`/missions/${mission.data.id}`);
    } catch (err) {
      toast.error(getErrorMessage(err, t('tripDetail.requestError')));
    } finally {
      setSubmittingRequest(false);
    }
  };

  const cancelTrip = async () => {
    if (!window.confirm(t('tripDetail.cancelConfirm'))) return;
    try {
      await api.delete(`/trips/${id}`);
      toast.success(t('tripDetail.cancelled'));
      navigate('/trips');
    } catch { toast.error(t('tripDetail.error')); }
  };

  const deleteTripPermanently = async () => {
    if (!window.confirm(t('tripDetail.deleteConfirm'))) return;
    try {
      await api.delete(`/trips/${id}/permanent`);
      toast.success(t('tripDetail.deleted'));
      navigate('/trips');
    } catch (err) {
      toast.error(getErrorMessage(err, t('tripDetail.error')));
    }
  };

  const isPublishedToday = trip.created_at
    && new Date(trip.created_at).toDateString() === new Date().toDateString();

  return (
    <DashboardShell>
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-4">
        <ArrowLeft size={16} className="mr-1" /> {t('tripDetail.back')}
      </Button>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent text-accent-foreground">
                    <TransportModeIcon mode={trip.transport_mode} size={22} />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold">{trip.departure_city} → {trip.arrival_city}</h1>
                    <div className="text-sm text-muted-foreground">{TRANSPORT_LABELS[trip.transport_mode]} • {formatDate(trip.departure_date)}</div>
              {isOwner && Number.isFinite(trip.views_30d) && (
                <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-secondary px-3 py-1.5 text-sm text-muted-foreground" data-testid="trip-views-count">
                  <Eye size={15} aria-hidden="true" />
                  <span>{t('listingViews.last30Days').replace('{count}', String(trip.views_30d))}</span>
                </div>
              )}
                  </div>
                </div>
                <StatusBadge status={trip.status} />
              </div>
              <div className="grid grid-cols-3 gap-2 text-sm mt-4">
                <div className="rounded-md bg-secondary p-3">
                  <div className="text-muted-foreground text-xs">{t('tripDetail.available')}</div>
                  <div className="font-semibold">{remaining} kg</div>
                </div>
                <div className="rounded-md bg-secondary p-3">
                  <div className="text-muted-foreground text-xs">{t('tripDetail.total')}</div>
                  <div className="font-semibold">{trip.capacity_kg} kg</div>
                </div>
                <div className="rounded-md bg-secondary p-3">
                  <div className="text-muted-foreground text-xs">{t('tripDetail.pricePerKg')}</div>
                  <div className="font-semibold">
                    {trip.price_per_kg ? formatPrice(trip.price_per_kg) : '—'}
                    {trip.negotiable && <span className="font-normal text-muted-foreground">{t('tripDetail.negotiable')}</span>}
                  </div>
                </div>
              </div>
              {(trip.dropoff_deadline_hours || (trip.availability_windows || []).length > 0) && <div className="mt-4 text-sm text-muted-foreground space-y-1">
                {trip.dropoff_deadline_hours && <p>{t('tripDetail.deadline').replace('{hours}', String(trip.dropoff_deadline_hours))}</p>}
                {(trip.availability_windows || []).map((availabilityWindow, index) => <p key={index}>{t('tripDetail.availability').replace('{start}', formatDate(availabilityWindow.start)).replace('{end}', formatDate(availabilityWindow.end))}</p>)}
              </div>}
              {trip.notes && <p className="text-sm text-muted-foreground mt-4">{trip.notes}</p>}
              {trip.max_dimensions_cm && (
                <div className="mt-4 text-sm">
                  <span className="font-medium">{t('tripDetail.maxDimensions')} </span>
                  <span className="text-muted-foreground">{trip.max_dimensions_cm} cm</span>
                </div>
              )}
              {(trip.accepted_parcel_types || []).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {trip.accepted_parcel_types.map((value) => {
                    const type = PARCEL_TYPES.find((parcelType) => parcelType.value === value);
                    return <span key={value} className="px-2 py-0.5 rounded-full text-xs border bg-secondary">{type?.label || value}</span>;
                  })}
                </div>
              )}
              {trip.restrictions && (
                <div className="mt-4 text-sm">
                  <span className="font-medium">{t('tripDetail.restrictions')} </span>
                  <span className="text-muted-foreground">{trip.restrictions}</span>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><MapPin size={16} /> {t('tripDetail.route')}</CardTitle></CardHeader>
            <CardContent>
              <MapPanel departure={trip.departure_city} arrival={trip.arrival_city} height="260px" />
            </CardContent>
          </Card>

          {isOwner && matches.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><Sparkles size={16} className="text-primary" /> {t('tripDetail.matchesTitle').replace('{count}', String(matches.length))}</CardTitle></CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 gap-3">
                  {matches.slice(0, 6).map(match => (
                    <div key={match.id} className="relative">
                      <ParcelCard parcel={match} />
                      {match.match_score != null && <Badge className="absolute right-2 top-2 bg-emerald-100 text-emerald-800 border-0">{t('tripDetail.compatible').replace('{score}', String(Math.round(match.match_score)))}</Badge>}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-base">{t('tripDetail.traveler')}</CardTitle></CardHeader>
            <CardContent>
              <Link to={`/users/${trip.traveler_id}`} className="flex items-center gap-3 mb-3 group">
                <Avatar className="group-hover:ring-2 group-hover:ring-primary transition-shadow">
                  <AvatarImage src={travelerProfile?.avatar_url || ''} />
                  <AvatarFallback className="bg-secondary">{(trip.traveler_name || 'U').split(' ').map(n => n[0]).slice(0,2).join('')}</AvatarFallback>
                </Avatar>
                <div>
                  <div className="font-semibold group-hover:text-primary group-hover:underline flex items-center gap-1.5">
                    {trip.traveler_name}
                    {travelerProfile?.deliverer_validated && <BadgeCheck size={14} className="text-emerald-600" />}
                  </div>
                  {trip.traveler_rating > 0 && (
                    <div className="text-xs text-muted-foreground flex items-center gap-0.5">
                      <Star size={11} className="fill-amber-500 text-amber-500" /> {trip.traveler_rating.toFixed(1)}
                      {travelerProfile?.review_count > 0 && ` (${t('tripDetail.reviewCount').replace('{count}', String(travelerProfile.review_count))})`}
                    </div>
                  )}
                  {(travelerProfile?.city || travelerProfile?.country) && (
                    <div className="text-xs text-muted-foreground">{[travelerProfile.city, travelerProfile.country].filter(Boolean).join(', ')}</div>
                  )}
                </div>
              </Link>
              {!isOwner && (
                <Button variant="outline" className="w-full" onClick={() => navigate(`/messages/${trip.traveler_id}`)}>
                  <MessageCircle size={14} className="mr-1" /> {t('tripDetail.contact')}
                </Button>
              )}
            </CardContent>
          </Card>

          {!isOwner && trip.status === 'active' && (
            <Card>
              <CardHeader><CardTitle className="text-base">{t('tripDetail.reserve')}</CardTitle></CardHeader>
              <CardContent>
                <Dialog open={requestOpen} onOpenChange={setRequestOpen}>
                  <DialogTrigger asChild>
                    <Button className="w-full" data-testid="public-trip-request-button">{t('tripDetail.requestButton')}</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>{t('tripDetail.requestTitle')}</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                      <div>
                        <Label>{t('tripDetail.chooseParcel')}</Label>
                        {myDeliveries.length === 0 ? (
                          <div className="text-sm text-muted-foreground">{t('tripDetail.noParcels')} <a className="text-primary underline" onClick={() => navigate('/deliveries/new')}>{t('tripDetail.postParcel')}</a>.</div>
                        ) : (
                          <select className="w-full border rounded-md p-2 text-sm" value={selectedDeliveryId} onChange={e => setSelectedDeliveryId(e.target.value)} data-testid="request-delivery-select">
                            <option value="">{t('tripDetail.choosePlaceholder')}</option>
                            {myDeliveries.map((d) => {
                              const deliveryId = getDeliveryId(d);
                              return (
                                <option key={deliveryId} value={deliveryId}>
                                  {getDeliveryTitle(d) || t('tripDetail.parcelFallback').replace('{id}', deliveryId)} ({getDeliveryWeight(d)} kg)
                                </option>
                              );
                            })}
                          </select>
                        )}
                      </div>
                      <div>
                        <Label>{t('tripDetail.proposedPrice')}</Label>
                        <Input type="number" value={requestPrice} onChange={e => setRequestPrice(e.target.value)} data-testid="request-price-input" />
                      </div>
                      <div>
                        <Label>{t('tripDetail.message')}</Label>
                        <Textarea value={requestMessage} onChange={e => setRequestMessage(e.target.value)} />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setRequestOpen(false)}>{t('tripDetail.cancel')}</Button>
                      <Button onClick={submitRequest} disabled={submittingRequest} data-testid="request-submit-button">{t('tripDetail.send')}</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>
          )}

          {isOwner && ['active', 'completed'].includes(trip.status) && (
            <Card>
              <CardHeader><CardTitle className="text-base">{t('tripDetail.manage')}</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full gap-1.5"
                  onClick={openTripEditDialog}
                  data-testid="edit-trip-button"
                >
                  <Edit3 size={14} /> {t(trip.status === 'completed' ? 'tripDetail.reopen' : 'tripDetail.editInfo')}
                </Button>
                {trip.status === 'active' && (
                  <Button variant="destructive" className="w-full" onClick={cancelTrip} data-testid="cancel-trip-button">
                    {t('tripDetail.cancelTrip')}
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {isOwner && isPublishedToday && (
            <Card>
              <CardHeader><CardTitle className="text-base">{t('tripDetail.permanentDelete')}</CardTitle></CardHeader>
              <CardContent>
                <Button
                  variant="destructive"
                  className="w-full gap-1.5"
                  onClick={deleteTripPermanently}
                  data-testid="delete-trip-permanent-button"
                >
                  <Trash2 size={14} /> {t('tripDetail.deleteForever')}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Dialog edit trip */}
          {isOwner && (
            <Dialog open={editTripOpen} onOpenChange={setEditTripOpen}>
              <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{t('tripDetail.editTitle')}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSaveTripEdit} className="space-y-4 pt-2">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="trip-edit-dep-date">{t('tripDetail.departureDate')}</Label>
                      <Input
                        id="trip-edit-dep-date"
                        type="datetime-local"
                        required
                        value={editTripForm.departure_date || ''}
                        onChange={e => setEditTripForm(f => ({ ...f, departure_date: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="trip-edit-arr-date">{t('tripDetail.arrivalDate')}</Label>
                      <Input
                        id="trip-edit-arr-date"
                        type="datetime-local"
                        value={editTripForm.arrival_date || ''}
                        onChange={e => setEditTripForm(f => ({ ...f, arrival_date: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="trip-edit-mode">{t('tripDetail.transport')}</Label>
                      <select
                        id="trip-edit-mode"
                        value={editTripForm.transport_mode || 'car'}
                        onChange={e => setEditTripForm(f => ({ ...f, transport_mode: e.target.value }))}
                        className="w-full border rounded-md p-2 text-sm mt-1 bg-background text-foreground"
                      >
                        <option value="car">{t('tripDetail.car')}</option>
                        <option value="plane">{t('tripDetail.plane')}</option>
                        <option value="train">{t('tripDetail.train')}</option>
                        <option value="bus">{t('tripDetail.bus')}</option>
                        <option value="boat">{t('tripDetail.boat')}</option>
                        <option value="truck">{t('tripDetail.truck')}</option>
                        <option value="motorcycle">{t('tripDetail.motorcycle')}</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="trip-edit-cap">{t('tripDetail.capacity')}</Label>
                      <Input
                        id="trip-edit-cap"
                        type="number"
                        min={1}
                        required
                        value={editTripForm.capacity_kg || ''}
                        onChange={e => setEditTripForm(f => ({ ...f, capacity_kg: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="trip-edit-pkg">{t('tripDetail.priceKg')}</Label>
                      <Input
                        id="trip-edit-pkg"
                        type="number"
                        step="0.5"
                        min={0}
                        placeholder={t('tripDetail.priceExample')}
                        value={editTripForm.price_per_kg || ''}
                        onChange={e => setEditTripForm(f => ({ ...f, price_per_kg: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="trip-edit-flat">{t('tripDetail.flatPrice')}</Label>
                      <Input
                        id="trip-edit-flat"
                        type="number"
                        step="0.5"
                        min={0}
                        placeholder={t('tripDetail.flatPriceExample')}
                        value={editTripForm.flat_price || ''}
                        onChange={e => setEditTripForm(f => ({ ...f, flat_price: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="trip-edit-cities">{t('tripDetail.stops')}</Label>
                      <Input
                        id="trip-edit-cities"
                        placeholder={t('tripDetail.stopsExample')}
                        value={editTripForm.intermediate_cities || ''}
                        onChange={e => setEditTripForm(f => ({ ...f, intermediate_cities: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="trip-edit-notes">{t('tripDetail.notes')}</Label>
                      <Textarea
                        id="trip-edit-notes"
                        rows={2}
                        placeholder={t('tripDetail.notesExample')}
                        value={editTripForm.notes || ''}
                        onChange={e => setEditTripForm(f => ({ ...f, notes: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="trip-edit-restrictions">{t('tripDetail.restrictionsOptional')}</Label>
                      <Textarea
                        id="trip-edit-restrictions"
                        rows={2}
                        placeholder={t('tripDetail.restrictionsExample')}
                        value={editTripForm.restrictions || ''}
                        onChange={e => setEditTripForm(f => ({ ...f, restrictions: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="trip-edit-dims">{t('tripDetail.dimensions')}</Label>
                      <Input
                        id="trip-edit-dims"
                        placeholder="50x40x20"
                        value={editTripForm.max_dimensions_cm || ''}
                        onChange={e => setEditTripForm(f => ({ ...f, max_dimensions_cm: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <ParcelTypeCheckboxes legend={t('tripDetail.acceptedTypes')} value={editTripForm.accepted_parcel_types || []} onChange={value => setEditTripForm(current => ({ ...current, accepted_parcel_types: value }))} testIdPrefix="trip-edit-type" />
                    </div>
                    <div className="sm:col-span-2 flex items-center gap-2">
                      <Checkbox
                        id="trip-edit-negotiable"
                        checked={editTripForm.negotiable}
                        onCheckedChange={v => setEditTripForm(f => ({ ...f, negotiable: v }))}
                      />
                      <Label htmlFor="trip-edit-negotiable" className="cursor-pointer">{t('tripDetail.negotiableLabel')}</Label>
                    </div>
                  </div>
                  <DialogFooter className="pt-3">
                    <Button type="button" variant="outline" onClick={() => setEditTripOpen(false)}>
                      {t('tripDetail.cancel')}
                    </Button>
                    <Button type="submit" disabled={savingTripEdit}>
                      {savingTripEdit ? t('tripDetail.saving') : t('tripDetail.saveChanges')}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>
    </DashboardShell>
  );
}
